import type { ShipmentRate } from "@constants/formDefaults";
import {
  Alert,
  Box,
  CardContent,
  CardHeader,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { formatCurrency } from "@utils/currencyUtils";
import { getCountries, getStates } from "@utils/locationData";
import { Controller, useFormContext } from "react-hook-form";

interface ShippingAddressProps {
  hasAttemptedValidation?: boolean;
  shippingError?: string | null;
  title?: string;
  isAuthenticated?: boolean;
}

export default function ShippingAddress({
  hasAttemptedValidation = false,
  shippingError,
  title = "4. Please enter your shipping address",
  isAuthenticated = false,
}: ShippingAddressProps) {
  const {
    control,
    watch,
    register,
    formState: { errors },
  } = useFormContext();

  const countryCode = watch("country") || "US";
  const availableOptions = watch("shippingOptions") || [];
  const fullName = watch("fullName");
  const email = watch("email");
  const phoneNumber = watch("phoneNumber");
  const countries = getCountries();
  const states = getStates(countryCode);

  return (
    <Box>
      <CardHeader title={title} slotProps={{ title: { variant: "h5" } }} />

      {/* Contact information section - only for authenticated users */}
      {isAuthenticated && (
        <CardContent>
          <Stack spacing={3}>
            <TextField
              label="Full Name*"
              fullWidth
              {...register("fullName", { required: "Full name is required" })}
              error={!!errors.fullName}
              helperText={errors.fullName?.message?.toString()}
              slotProps={{ inputLabel: { shrink: !!fullName } }}
            />
            <TextField
              label="Email*"
              fullWidth
              type="email"
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Please enter a valid email address",
                },
              })}
              error={!!errors.email}
              helperText={errors.email?.message?.toString()}
              slotProps={{ inputLabel: { shrink: !!email } }}
            />
            <TextField
              label="Phone Number"
              fullWidth
              type="tel"
              {...register("phoneNumber", {
                pattern: {
                  value: /^[\d\s\-+()]+$/,
                  message: "Please enter a valid phone number",
                },
                minLength: {
                  value: 10,
                  message: "Phone number must be at least 10 digits",
                },
              })}
              error={!!errors.phoneNumber}
              helperText={errors.phoneNumber?.message?.toString()}
              slotProps={{ inputLabel: { shrink: !!phoneNumber } }}
            />
          </Stack>
        </CardContent>
      )}

      <CardContent>
        <Stack spacing={3}>
          <TextField
            label="Address 1*"
            fullWidth
            {...register("address1", { required: "Address is required" })}
            error={!!errors.address1}
            helperText={errors.address1?.message?.toString()}
          />
          <TextField label="Address 2" fullWidth {...register("address2")} />

          <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
            <TextField
              label="City*"
              fullWidth
              {...register("city", { required: "City is required" })}
              error={!!errors.city}
              helperText={errors.city?.message?.toString()}
            />
            <Controller
              name="country"
              control={control}
              rules={{ required: "Country is required" }}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.country}>
                  <InputLabel>Country*</InputLabel>
                  <Select {...field} label="Country*">
                    {countries.map((country) => (
                      <MenuItem key={country.code} value={country.code}>
                        {country.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {hasAttemptedValidation && errors.country?.message?.toString()}
                  </FormHelperText>
                </FormControl>
              )}
            />
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
            <Controller
              name="state"
              control={control}
              rules={{ required: "State is required" }}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.state}>
                  <InputLabel>State/Province/Region*</InputLabel>
                  <Select {...field} label="State/Province/Region*">
                    {states.map((state) => (
                      <MenuItem key={state.subCode} value={state.subCode}>
                        {state.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {hasAttemptedValidation && errors.state?.message?.toString()}
                  </FormHelperText>
                </FormControl>
              )}
            />
            <TextField
              label="Postal Code/Zip Code*"
              fullWidth
              {...register("postalCode", { required: "Postal Code/Zip Code is required" })}
              error={hasAttemptedValidation && !!errors.postalCode}
              helperText={hasAttemptedValidation && errors.postalCode?.message?.toString()}
            />
          </Stack>
        </Stack>
      </CardContent>

      {/* Show shipping error if API call failed */}
      {shippingError && (
        <CardContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            Sorry but we're unable to calculate shipping costs for the address you entered. Please
            edit the address and try again.
          </Alert>
        </CardContent>
      )}

      {availableOptions.length > 0 && (
        <CardHeader
          title="Select your shipping option below"
          slotProps={{ title: { variant: "h4" } }}
        />
      )}

      <CardContent>
        {!availableOptions.length && (
          <Typography gutterBottom>
            Please click "Calculate Shipping" first, then choose one of the available options.
          </Typography>
        )}
        <FormControl
          component="fieldset"
          sx={{ px: 0 }}
          error={hasAttemptedValidation && !!errors.shipmentRate}
        >
          <Controller
            name="shipmentRate"
            control={control}
            rules={{
              required:
                'Please click "Calculate Shipping" first, then choose one of the available options.',
            }}
            render={({ field }) => (
              <RadioGroup
                value={field.value?.serviceCode || ""}
                onChange={(e) => {
                  const selectedOption: ShipmentRate = availableOptions.find(
                    (opt: ShipmentRate) => opt.serviceCode === e.target.value,
                  );
                  field.onChange(selectedOption || null);
                  // CartInfoContext automatically includes shipmentRate in optimistic line items
                }}
              >
                {availableOptions.map((option: ShipmentRate) => (
                  <FormControlLabel
                    key={option.serviceCode}
                    value={option.serviceCode}
                    control={<Radio />}
                    label={`${option.serviceName} – ${formatCurrency(option.totalCost, option.currency)}`}
                  />
                ))}
              </RadioGroup>
            )}
          />
          {hasAttemptedValidation && errors.shipmentRate && (
            <FormHelperText>{errors.shipmentRate.message?.toString()}</FormHelperText>
          )}
        </FormControl>
      </CardContent>
    </Box>
  );
}
